/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Button, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { Fragment, useMemo, useState } from 'react'

import {
  CaracteristicasLesaoPanel,
  CaracteristicasLesaoPanelProps,
} from './components/caracteristicas-lesao/CaracteristicasLesaoPanel'
import EditarTecidosMolesFormModal from './EditarTecidosMolesFormModal'
import { TecidosMolesFieldModel } from './TecidosMolesField'
import TecidosMolesTableRow from './TecidosMolesTableRow'

export interface TecidosMolesTableProps {
  items?: TecidosMolesFieldModel[]
  cidadaoDataNascimento?: LocalDate
  updateItem?(item: TecidosMolesFieldModel): void
  removeItem?(item: TecidosMolesFieldModel): void
}

export default function TecidosMolesTable(props: TecidosMolesTableProps) {
  const { items, cidadaoDataNascimento, updateItem, removeItem } = props

  const [modalItem, setModalItem] = useState<TecidosMolesFieldModel>(undefined)

  const itemsDisabled = useMemo(
    () =>
      items
        ?.map((item) => item?.procedimentos)
        .flat()
        .filter((item) => !modalItem?.procedimentos?.map((proced) => proced?.id).includes(item?.id)),
    [items, modalItem]
  )

  const renderProcedimentos = (item: TecidosMolesFieldModel) =>
    !!item.procedimentos?.length ? (
      <VFlow vSpacing={0}>
        {item.procedimentos.map((proced) => (
          <Text key={proced.id}>
            {proced.descricao} - {proced.codigo}
          </Text>
        ))}
      </VFlow>
    ) : (
      <Text>-</Text>
    )

  const renderActions = (item: TecidosMolesFieldModel) => (
    <HFlow hSpacing={0.5} justifyContent='flex-end'>
      <Tooltip text='Editar'>
        <Button
          size='small'
          skin='ghost'
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            setModalItem(item)
          }}
        >
          <Icon icon='penOutline' />
        </Button>
      </Tooltip>
      <Tooltip text='Excluir'>
        <Button
          size='small'
          skin='ghost'
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            removeItem(item)
          }}
        >
          <Icon icon='trashOutline' />
        </Button>
      </Tooltip>
    </HFlow>
  )

  return (
    <Fragment>
      <AccordionDataTable<TecidosMolesFieldModel>
        columns={[
          {
            name: 'local',
            header: 'Local',
            render: (item) => <Text>{item.local.label}</Text>,
            size: 2,
            xl: 1,
          },
          {
            name: 'problemas',
            header: 'CID 10',
            render: (item) => <Text>{item.problema ? `${item.problema.nome} - ${item.problema.codigo}` : '-'}</Text>,
            size: 4,
          },
          {
            name: 'procedimentos',
            header: 'Procedimentos',
            render: renderProcedimentos,
            size: 4,
            xl: 5,
          },
          {
            name: 'actions',
            render: renderActions,
            size: 2,
          },
        ]}
        rows={items}
        components={{
          Row: TecidosMolesTableRow,
          AccordionPanel: ({ row }) => (
            <CaracteristicasLesaoPanel {...convertCaracteristicaLesaoFieldModelToPanel(row)} />
          ),
        }}
        disableRow={(row) => !row.caracteristicasLesao}
        disableRowMessage='Não foram inseridas características da lesão nesse registro'
      />

      <EditarTecidosMolesFormModal
        open={!!modalItem}
        cidadaoDataNascimento={cidadaoDataNascimento}
        initialValues={modalItem}
        procedimentosSelecionados={itemsDisabled}
        onSubmit={updateItem}
        onClose={() => setModalItem(undefined)}
      />
    </Fragment>
  )
}

const convertCaracteristicaLesaoFieldModelToPanel = (row: TecidosMolesFieldModel) =>
  ({
    items:
      row.caracteristicasLesao &&
      [
        row.caracteristicasLesao.base,
        row.caracteristicasLesao.consistencia,
        row.caracteristicasLesao.cor,
        row.caracteristicasLesao.lesaoFundamental?.lesaoEnvolvida,
        row.caracteristicasLesao.quantidade,
        row.caracteristicasLesao.sintomatologia?.value !== 'outros' && row.caracteristicasLesao.sintomatologia?.value,
        row.caracteristicasLesao.superficie?.value,
        row.caracteristicasLesao.tamanho,
      ].filter((item) => item),
    descricaoSintomatologia: row.caracteristicasLesao?.sintomatologia?.descricaoOutro,
  } as CaracteristicasLesaoPanelProps)
